<!--
  Canonical Product Editor - single-stage architecture.

  Originated as the 2026-06-13 design-bundle shell and graduated to the
  canonical /distributor/product-editor/:id route on Day 6.5 of the
  feature build (the legacy hi-fi ProductEditor.vue + components/ +
  sections/DecorationsHotspot chain was deleted at the same time).

  Plan: planning/design-handoffs/handoff_2026-06-13/implementation-plan/
        02-product-editor.md

  Day 6 (audit gap - 'variants below preview'): the body becomes a 3-col
  layout when a placement is selected. The right rail mounts the
  DecorationInspector. When no placement is selected, the right rail is
  not rendered and the body stays 2-col.

  Layout (placement selected):
    +-----------------------------------------------+
    | header (slim, app shell)                      |
    +------+---------------------+------------------+
    |      | ReviewBanner        |                  |
    |      | Stage (persistent)  |                  |
    | Left | RoutingStrip        | DecorationInspector
    | Rail | ColorFilmstrip      | (right rail)     |
    |      | ContextPanel        |                  |
    +------+---------------------+------------------+
    | readiness banner                              |
    +-----------------------------------------------+

  Shell responsibilities:
    - 3-region layout
    - Loading / error gates from productEditor Pinia store
    - Lifecycle wiring (open/close/load)
    - No section behavior - children own their content
-->
<template>
    <div data-testid="product-editor-root" class="product-editor-root">
        <EditorPagehead
            v-if="!store.loading && !store.loadError"
            data-testid="product-editor-header"
            class="product-editor-header"
        />

        <v-progress-linear
            v-if="store.loading"
            indeterminate
            data-testid="product-editor-loading"
        />
        <!--
          Bug #81: a 404 from the campaign_product GET is most often a
          tenant mismatch (SA impersonating a distributor that doesn't
          own the URL'd product) or a deleted product. Either way,
          "Retry" doesn't fix it — the SA needs an explicit empty state
          with actionable next steps. Non-404 errors keep the existing
          generic alert so transient network failures stay retryable.
        -->
        <ProductEditorNotFound
            v-else-if="isLoadError404"
        />
        <v-alert
            v-else-if="store.loadError"
            type="error"
            data-testid="product-editor-load-error"
        >
            Failed to load product.
            <button data-testid="product-editor-retry" @click="retry">Retry</button>
        </v-alert>

        <div v-else class="product-editor-body">
            <LeftRail data-testid="product-editor-left-rail" class="product-editor-rail" />
            <div class="product-editor-main">
                <!--
                  Day 8 Item A1: SA-without-impersonation banner always
                  mounts at the top of the main column. The component
                  internally gates visibility on (isSuperAdmin AND not
                  impersonating); non-SAs and impersonating SAs see
                  nothing.
                -->
                <SaImpersonationBanner
                    data-testid="product-editor-sa-impersonation-banner"
                />
                <!--
                  Stage/ColorFilmstrip context scope (revised 2026-07-14 for
                  the Sizes & Colors merge; Finding 2): the Stage +
                  ColorFilmstrip render when `showStagePreview` is true --
                  i.e. a decoration placement is selected OR the Sizes &
                  Colors setup tab is active (colors now live on that merged
                  tab). On the Supplier & SKU / Pricing / Storefront setup
                  tabs they are hidden and the full middle column goes to the
                  ContextPanel (they were crushing the setup panel into a
                  ~32px sliver). RoutingStrip stays placement-gated.

                  Day 8 round-1 P1.2 fix: ReviewBanner pulled OUT of
                  the hasPlacementSelection gate. It is always rendered
                  in the middle column; its OWN internal v-if (count > 0)
                  decides whether to display, so non-approved artwork
                  remains visible to the user regardless of which tab
                  is active. Gating it on placement selection silently
                  hid review work that needed attention on the Product
                  Setup tabs.
                -->
                <!--
                  ============================================================
                  TEMPORARILY HIDDEN (2026-07-20, Jake) -- RESTORE WITH THE
                  APPROVAL WORKFLOW UI.
                  ============================================================
                  The ReviewBanner ("N decoration(s) use artwork that isn't
                  approved yet -- <names>", with a "Review" CTA) is hidden until
                  the decoration APPROVAL workflow UI is built. Its "Review" CTA
                  is a no-op stub today (points at an Approval workspace that does
                  not exist yet), and its "not approved" state is driven by a
                  PLACEHOLDER deterministic-hash lifecycle (productLifecycleFor /
                  ADR-0010 interim), NOT a real approval state -- so it flags
                  every product that has a decoration regardless of true status,
                  which is noise until the workflow is real.

                  *** WHEN PLANNING THE APPROVAL WORKFLOW UI, INCLUDE RESTORING
                  THIS BANNER AS AN EXPLICIT DELIVERABLE. *** Restore =
                  uncomment the <ReviewBanner> block below, wire the "Review" CTA
                  to the real Approval workspace route, and swap the placeholder
                  lifecycle for the real approval state. ReviewBanner.vue and its
                  unit tests are left INTACT so it re-mounts cleanly.
                  ============================================================
                <ReviewBanner
                    data-testid="product-editor-review-banner"
                    class="product-editor-review-banner"
                />
                -->

                <!--
                  Stage + ColorFilmstrip render when `showStagePreview` is
                  true (Finding 2, revised 2026-07-14): on a decoration tab
                  (a placement is selected) the Stage composites the
                  decoration; on the Sizes & Colors setup tab it shows the
                  live bare-garment preview so the user can browse colors
                  before decorating. The bare-render path in Stage
                  (artworkFileId=null) calls Artifi's GetImage without a
                  logo overlay; Stage's auto-pick watcher lands the first
                  available color when none is selected. On the other setup
                  tabs (Supplier & SKU, Pricing, Storefront) both surfaces
                  are hidden so the full middle column goes to the
                  ContextPanel instead of crushing it into a sliver.

                  RoutingStrip and DecorationInspector are
                  decoration-specific surfaces and only render when a
                  placement is selected -- routing has no meaning
                  without a decoration, and the inspector shows
                  per-decoration controls.
                -->
                <!--
                  Sizes & Colors side-by-side, colors full-width below
                  (2026-07-20, Jake; row-2 follow-up to #2183/#2185): on the
                  merged Sizes & Colors setup tab the layout is:
                    ROW 1 (2-col grid): [ Stage + ColorFilmstrip ] [ SizesPanel ]
                    ROW 2 (full width) : [ ColorsPanel ]
                  The canvas is enlarged for the 800x1200 Artifi renders (#2177
                  -- Stage downscales the larger source with object-fit:
                  contain, so a bigger canvas is crisper without upscale blur),
                  but shrunk from the first-pass clamp: the color list can grow
                  tall (some SKUs have many colors), so ColorsPanel gets its own
                  full-width row instead of being squeezed into the narrow sizes
                  column. `isSizesColorsSplit` gates this on the sizes-colors tab
                  with NO placement selected; every other tab (and any
                  decoration placement) falls through to the default vertical
                  stack below (where ContextPanel still stacks SizesPanel +
                  ColorsPanel together). The split mounts SizesPanel and
                  ColorsPanel DIRECTLY (not via ContextPanel) precisely so
                  ColorsPanel can leave the grid and go full-width. The sizes
                  column keeps data-testid="product-editor-context-panel" for
                  continuity with the shell's layout assertions. Below a narrow
                  breakpoint row 1 collapses to a single stacked column so it
                  never overflows the viewport horizontally; row 2 stays
                  full-width below.
                -->
                <div
                    v-if="isSizesColorsSplit"
                    data-testid="product-editor-sizes-colors-split"
                    class="product-editor-sizes-colors-split is-split-v2"
                >
                    <div class="product-editor-split-row-top">
                        <div class="product-editor-split-preview">
                            <Stage
                                data-testid="product-editor-stage"
                                class="product-editor-stage product-editor-stage--split"
                            />
                        </div>
                        <div
                            data-testid="product-editor-context-panel"
                            class="product-editor-split-sizes product-editor-context"
                        >
                            <SizesPanel />
                        </div>
                    </div>
                    <ColorFilmstrip
                        data-testid="product-editor-color-filmstrip"
                        class="product-editor-filmstrip"
                    />
                    <div
                        data-testid="product-editor-split-colors"
                        class="product-editor-split-colors product-editor-context"
                    >
                        <ColorsPanel />
                    </div>
                </div>
                <template v-else>
                    <Stage
                        v-if="showStagePreview"
                        data-testid="product-editor-stage"
                        class="product-editor-stage"
                    />
                    <RoutingStrip
                        v-if="hasPlacementSelection"
                        data-testid="product-editor-routing-strip"
                        class="product-editor-routing-strip"
                    />
                    <ColorFilmstrip
                        v-if="showStagePreview"
                        data-testid="product-editor-color-filmstrip"
                        class="product-editor-filmstrip"
                    />
                    <ContextPanel
                        data-testid="product-editor-context-panel"
                        class="product-editor-context"
                    />
                </template>
            </div>
            <aside
                v-if="hasPlacementSelection"
                data-testid="product-editor-right-rail"
                class="product-editor-right"
            >
                <DecorationInspector />
            </aside>
        </div>

        <ReadinessBanner
            v-if="!store.loading && !store.loadError"
            data-testid="product-editor-readiness-banner"
            class="product-editor-banner"
        />
        <!--
          Discard-on-leave confirmation (Jake, 2026-08-27). Opens when the
          user leaves a product that has no SKU (card #233), or an untouched
          product whose SKU was auto-filled -- see discardStubOnLeave.js. The
          wording depends on whether the user did any work on it. Cancel
          keeps the navigation on this page; Discard deletes the row and then
          leaves.
        -->
        <DestructiveConfirmDialog
            v-model="discardDialogOpen"
            title="Discard this product?"
            :description="discardDialogCopy.description"
            :consequences="discardDialogCopy.consequences"
            confirm-label="Discard and leave"
            cancel-label="Stay on this page"
            confirm-color="error"
            @confirm="onDiscardConfirm"
            @cancel="onDiscardCancel"
        />
        <!--
          Leaving with an EMPTY SHADE (Jake, 2026-10-21): orders skip it, so
          garments of that shade are produced with no decoration there.
          Non-blocking -- "Leave anyway" goes -- but deliberately in the way,
          and it says how to fix each one.
        -->
        <DestructiveConfirmDialog
            v-model="emptyShadeDialogOpen"
            title="Some garments will get no decoration"
            :description="emptyShadeDescription"
            :consequences="emptyShadeConsequences"
            confirm-label="Leave anyway"
            cancel-label="Stay and fix it"
            confirm-color="warning"
            @confirm="settleEmptyShadeDecision(true)"
            @cancel="settleEmptyShadeDecision(false)"
        >
            <p v-if="emptyShades.length > 0" data-testid="empty-shade-leave-howto">
                To fix it, open the placement and use Replace on the empty
                card to add a decoration, or choose Use for all colors on the
                light-garment card to put its decoration on every color.
            </p>
            <p v-if="uncoveredColors.length > 0" data-testid="uncovered-colors-leave-howto">
                To fix a color with no placement, add a placement for it, or
                open an existing placement and choose Use for all colors so
                its decoration goes on every color.
            </p>
        </DestructiveConfirmDialog>
    </div>
</template>

<script setup>
import { describeError } from "@/utils/safeErrorLog";
import { computed, onMounted, onUnmounted, provide, ref, watch } from "vue";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";
import axiosInstance from "@/axios";
import { useStore as useVuexStore } from "vuex";
import { useProductEditorStore } from "@/stores/productEditor";
import {
    useProductEditorUiState,
    LEFT_RAIL_ITEMS,
} from "@/stores/productEditorUiState";
import { useProductEditorRouteSync } from "./useProductEditorRouteSync";
import { useArtworksStore } from "@/stores/artworks";
import { useSanmarProductMeta } from "@/composables/useSanmarProductMeta";
import { useStorefrontColorRenders } from "@/composables/useStorefrontColorRenders";
import { useSkuSwapCleanup } from "./useSkuSwapCleanup";
import { awaitArtifiCoverage } from "@/composables/useArtifiCoverage";
import {
    artifiPreviewEligible,
    hasCatalogIntegration,
    supplierKeyForProduct,
} from "@/utils/supplier-entitlement";
import {
    SUPPLIER_OPTIONS_KEY,
    createSupplierOptions,
} from "@/composables/useSupplierOptions";
import EditorPagehead from "./EditorPagehead.vue";
import LeftRail from "./LeftRail.vue";
import Stage from "./Stage.vue";
import RoutingStrip from "./RoutingStrip.vue";
import ColorFilmstrip from "./ColorFilmstrip.vue";
import ContextPanel from "./ContextPanel.vue";
// SizesPanel + ColorsPanel are mounted DIRECTLY in the Sizes & Colors split
// (isSizesColorsSplit) so ColorsPanel can drop into a full-width row below the
// canvas+sizes grid row. Everywhere else ContextPanel still stacks them
// together (see ContextPanel.vue) -- these direct mounts are split-only.
import SizesPanel from "./sections/SizesPanel.vue";
import ColorsPanel from "./sections/ColorsPanel.vue";
import DecorationInspector from "./DecorationInspector.vue";
import ReadinessBanner from "./ReadinessBanner.vue";
import ReviewBanner from "./ReviewBanner.vue";
import SaImpersonationBanner from "./SaImpersonationBanner.vue";
import ProductEditorNotFound from "./ProductEditorNotFound.vue";
import DestructiveConfirmDialog from "./DestructiveConfirmDialog.vue";
import { createStubDiscardGuard, isLeavingEditor } from "./discardStubOnLeave";
import { emptyShadesOf } from "@/utils/emptyShade";
import { uncoveredColorsOf, selectedProductColors } from "@/utils/uncoveredColors";
import { ABANDONMENT_RELEVANT_FIELDS } from "./abandonedProductStub";

const route = useRoute();
const router = useRouter();
const store = useProductEditorStore();
const editorUi = useProductEditorUiState();
const artworks = useArtworksStore();
// The vendor list, as OPTIONS ({ value: supplier_key, title: name, vendorId }).
//
// The shell needs it because a CampaignProduct persists its supplier as
// `supplier_id` (FK to `vendors`) plus a denormalized `supplier_name` --
// there is no `supplier_key` column on it -- while every capability
// question downstream is asked of a KEY. `supplierKeyForProduct` resolves
// the FK against the SERVER's own rows (falling back to an exact name
// match), so a tenant-editable display name never has to be
// pattern-matched. See supplier-entitlement.js.
//
// ONE loader for the whole editor, PROVIDED to the panels (SupplierSkuPanel
// injects it). Before 2026-10-21 the shell and the panel each fetched the
// list once, with no retry, and a single failed request broke the editor
// until a reload: the shell resolved the product to no key (empty catalog,
// no previews) while the panel showed its raw 'sanmar' default (Jake's
// report). The loader retries transient failures, and a later successful
// `reload()` (the panel's Retry) re-resolves everything that reads it.
const supplierOptionsSource = createSupplierOptions();
provide(SUPPLIER_OPTIONS_KEY, supplierOptionsSource);

// `null` (not `[]`) is the pre-load sentinel. The distinction is
// load-bearing for the #2017 race: an EMPTY list is a real answer
// ("no vendors"), while "not fetched yet" must not be read as one. See
// `supplierCatalogState` below.
//
// A FINAL failure reads as `[]` -- RESOLVED, not pending. A product whose
// supplier we cannot identify must not hang every colors/sizes reader on
// "Loading colors..." forever (the #2017 race), so it resolves to the same
// quiet 'not-integrated' state a named non-integrated supplier produces.
// Unlike before, that is not a latch: a successful reload replaces it.
const supplierOptions = computed(() => {
    if (supplierOptionsSource.loaded.value) {
        return supplierOptionsSource.options.value ?? [];
    }
    if (supplierOptionsSource.failed.value) return [];
    return null;
});

// The supplier_key for this product, or null while unresolvable.
const supplierKey = computed(() => {
    if (supplierOptions.value === null) return null;
    return supplierKeyForProduct(
        store.getEffectiveAttribute("supplier_id"),
        store.getEffectiveAttribute("supplier_name"),
        supplierOptions.value,
    );
});

// The composable re-reads this per call, so a supplier swap routes the
// NEXT request to the new supplier rather than the one resolved at mount.
// `resolvedSupplierKey` is declared below with the gate it belongs to;
// the arrow defers the read, so ordering is not a problem.
const meta = useSanmarProductMeta(() => resolvedSupplierKey.value);

const campaignProductId = computed(() => route.params.campaignProductId);

// A decorated render for every selected color, 30s after the product was last
// saved or at once on a Stage switch, default-included in the Storefront
// listing (Jake 2026-10-06/07, card https://trello.com/c/Qlwx8mlT). Mounted
// HERE, not in the listing panel: the save debounce and Stage switches happen
// on every tab. Unmounting the editor cancels every pending render.
useStorefrontColorRenders();

// Bookmarkable tab + placement URLs (2026-07-15, Jake). The active tab /
// selected placement is now driven by the URL (:tab / /placements/:id) with
// two-way sync to selectedLeftRailItem. This composable owns:
//   - route -> store (deep-link / refresh restores the tab or placement)
//   - store -> route (LeftRail clicks update the URL)
//   - the bare-id redirect to the last-viewed (else first) tab
//   - loop-guarding so the two directions never ping-pong
// The bare-id redirect REPLACES the old computeInitialRailItem smart-landing
// watcher: the URL is now the single source of truth for the initial tab, and
// Jake's locked landing policy for a bare id is "last-viewed else first tab
// (supplier-sku)", not the first-incomplete-step heuristic.
useProductEditorRouteSync({ route, router, editorUi, store });

// Day 6: the right rail (DecorationInspector) renders only when a placement
// is selected. Gate on selectedPlacementId (truthy = a placement is active).
const hasPlacementSelection = computed(
    () => editorUi.selectedPlacementId != null,
);

// Finding 2 (2026-07-09; revised 2026-07-14 for the Sizes & Colors merge):
// the Stage + ColorFilmstrip previously rendered unconditionally in the
// middle column, crushing the ContextPanel (which hosts the rail-selected
// setup section) into a ~32px sliver on the setup tabs. Jake's decision:
// show the Stage preview only when it is contextually useful --
//   - a decoration placement is selected (the Stage composites it), OR
//   - the Sizes & Colors setup tab is active (color browsing needs the live
//     bare-garment preview -- colors now live on this merged tab).
// On the other setup tabs (Supplier & SKU / Pricing / Storefront) the full
// middle column goes to the ContextPanel.
const showStagePreview = computed(
    () =>
        hasPlacementSelection.value ||
        editorUi.selectedLeftRailItem === LEFT_RAIL_ITEMS.SIZES_COLORS,
);

// Sizes & Colors side-by-side (2026-07-20, Jake, #2183): on the merged Sizes &
// Colors setup tab (and ONLY there -- no decoration placement active) the
// preview canvas and the sizes section render side-by-side in an enlarged
// two-column grid instead of the default vertical stack. A selected placement
// takes over the middle column (Stage composites the decoration, the right
// rail hosts the inspector), so the split is suppressed when a placement is
// active even if the rail key still reads `sizes-colors`.
const isSizesColorsSplit = computed(
    () =>
        !hasPlacementSelection.value &&
        editorUi.selectedLeftRailItem === LEFT_RAIL_ITEMS.SIZES_COLORS,
);

// Bug #81: detect a 404 from the campaign_product GET. axios surfaces
// HTTP errors as { response: { status } }; loadError holds the raw error
// object set by productEditor.loadServerState's catch block. Any other
// error (network failure, 500, etc.) falls through to the existing
// generic alert so transient failures stay retryable.
const isLoadError404 = computed(
    () => store.loadError?.response?.status === 404,
);

// Full-bleed escape from DistributorLayout's `.dist-main` (Day 8 Item B1;
// revised 2026-07-20 for whole-page scroll). The surrounding shell applies
// `padding: 24px` + `overflow-y: auto` to .dist-main. We tag <body> with
// `tf-product-editor-active` for the editor's lifetime; the global style
// block below uses that hook to drop the dist-main padding (so the editor's
// header/footer are full-bleed) while KEEPING `.dist-main`'s `overflow-y:
// auto` -- the outer app-shell main region owns the page scroll and the
// whole editor scrolls as one column (Jake: "nothing pinned"). The class is
// removed on unmount so the rest of the distributor app shell behaves
// normally. No router changes required.
//
// Pattern mirrors StoreEditShell's `body.tf-role-client` selector
// (frontend/src/pages/distributor/store-editor/StoreEditShell.vue:655).
const FULLBLEED_BODY_CLASS = "tf-product-editor-active";

onMounted(() => {
    store.openEditor(campaignProductId.value);
    store.loadServerState();
    // Phase F PR-4: hydrate the tenant's Artwork repository in parallel
    // with the campaign_product load. The DecorationInspector uses
    // `artworks.lifecycleStateForVariant` + `artworks.getRepository` to
    // resolve real Artwork lifecycle state and the footer attribution
    // name; both fall back gracefully when the fetch is in flight or
    // returns empty (no Artwork rows seeded for this tenant yet).
    //
    // Fire-and-forget: the store internally guards against late
    // responses via its epoch counter, so we don't await here. Errors
    // are swallowed into store.error and surface as the fallback
    // (hash lifecycle + impersonation/Acme footer name).
    artworks.fetch();
    // Vendor list for the name->key resolution the supplier gate needs
    // (#3312). Fire-and-forget like the others: `supplierCatalogState`
    // holds at 'pending' until it settles, and settles it does either way
    // (the loader never rejects; its retries are bounded), so readiness
    // can never hang on this request.
    supplierOptionsSource.load();
    if (typeof document !== "undefined" && document.body) {
        document.body.classList.add(FULLBLEED_BODY_CLASS);
    }
    if (typeof window !== "undefined") {
        window.addEventListener("pagehide", onPageHide);
        window.addEventListener("beforeunload", onBeforeUnload);
    }
});

// Tab close / reload / navigating off the SPA. The route-leave guard never
// runs for these, and an awaited flush would be torn down with the page, so
// the store sends the pending autosave as keepalive requests instead.
// `pagehide` is the flush point because it is the last event every browser
// fires on the way out, including where beforeunload is skipped (mobile).
function onPageHide() {
    if (typeof store.flushFieldAutoSaveOnUnload === "function") {
        store.flushFieldAutoSaveOnUnload();
    }
}

// The one leave the keepalive flush cannot make safe: a newer edit queued
// behind an autosave that is still in flight. Both requests would reach the
// API at once and the older one can commit last, so ask the browser to show
// its "Leave site?" confirmation. Staying lets the flight finish and the
// queued edit follow it. Every other unsaved state leaves without a prompt.
function onBeforeUnload(event) {
    if (
        (typeof store.isFieldEditQueuedBehindFlight === "function"
            && store.isFieldEditQueuedBehindFlight())
        // An EMPTY SHADE too: the browser's own "Leave site?" is the only
        // prompt a tab close can show (it cannot carry our text); the
        // in-app leave dialog explains the fix for in-app navigation.
        || hasUndecoratedGarments.value
    ) {
        event.preventDefault();
        // Legacy browsers only honour the prompt through returnValue.
        event.returnValue = "";
    }
}

// Regression #2003 fix-forward: the SanMar supplier-meta PRODUCER lives at
// the editor level so it runs on product OPEN regardless of which left-rail
// tab is active.
//
// #2003 introduced supplierMetaReady = (store.supplierMeta != null) plus a
// loading guard so cross-section readers (ColorFilmstrip swatches, LeftRail
// colors/sizes counts, ColorsPanel grid) don't misread "not fetched yet" as
// "loaded and empty". But the ONLY producer of supplierMeta was
// SupplierSkuPanel, which ContextPanel v-if-mounts ONLY on the Supplier &
// SKU tab. Landing on / switching to the Colors tab (or any non-SupplierSku
// tab) meant the producer never mounted, supplierMeta stayed null, and
// ColorsPanel hung on "Loading colors..." forever.
//
// Design (docs/design-philosophy.md: single-source state + one write model):
// the store owns supplierMeta; the producer that writes it must run wherever
// the state is consumed, not be trapped behind one tab's mount. So the shell
// is the sole store-meta writer. SupplierSkuPanel keeps its own local
// useSanmarProductMeta instance for its grid + catalog auto-fill (product
// name / descriptions) but no longer writes supplierMeta to the store --
// that removes the dual-writer race where a later SupplierSkuPanel mount
// could flip supplierMetaLoading back to true on the Colors tab.
//
// We key the fetch on the EFFECTIVE SKU (dirty-over-serverState) so it fires
// on open AND on an unsaved SKU swap (SupplierSkuPanel.applySwap writes
// product_item_number via updateField). loadServerState is fire-and-forget,
// so the immediate watch re-runs when the SKU lands after the async load.
//
// Supplier gate (2026-07-10, Jake -- P0 regression fix, store product 58
// PC54; re-keyed onto supplier_key 2026-09-06, GH #3312): only a supplier
// with a catalog integration behind it has anything to answer the meta
// proxy (GET /api/supplier/<key>/product/<sku>) with. For a NAMED
// non-integrated product (e.g. supplier "[RICH] Hard Stuff Inc") the SKU is
// unknown to every adapter, so the proxy 404s -- console noise with no
// payoff.
//
// WHY KEYS AND NOT THE NAME (#3312)
// ---------------------------------
// This gate used to be `isSanmarSupplierName(supplier_name)` -- a
// `/sanmar/i` regex over the DISPLAY NAME. That was correct only while
// SanMar was the sole integration, and its own docstring said so
// ("will WRONGLY suppress future SNS/PCNA products ... acceptable NOW
// because no SNS/PCNA products exist yet"). S&S products now exist, so
// the stated precondition expired:
//
//   isSanmarSupplierName('S&S Activewear') === false
//     -> setSupplierMeta({}) -> a resolved-EMPTY catalog
//     -> ~27 downstream supplierMeta readers concluded "loaded, and there
//        is nothing" -> NO colors and NO sizes for S&S.
//
// Measured on 8083 while the editor showed none of it: sns/3001 returns
// 96 colors and 9 sizes (BELLA+CANVAS 3001); sns/5000 returns 83 and 8.
//
// A display name is also not an identity: the regex matched "SanMar West",
// a DIFFERENT vendor, and sent its SKUs to SanMar's catalog. Keys are the
// vocabulary everything else already speaks -- entitlement resolves keys,
// the catalog routes validate keys, tf-deconetwork carries the key on the
// wire. See supplier-entitlement.js for the full argument.
//
// WHY THIS IS A TRI-STATE AND NOT A BOOLEAN
// -----------------------------------------
// The vendor list lands a round trip after mount, so there is a window in
// which a name cannot YET be resolved to a key. Collapsing that window
// into "not integrated" would publish {} for a product that is about to
// resolve to SanMar or S&S -- latching an empty catalog permanently and
// reopening #2017 from the other side. So:
//
//   'pending'        -> no fetch, and NO {} publish. Readiness stays
//                       false, exactly as it does during a real fetch.
//   'integrated'     -> fetch, against the RESOLVED key.
//   'not-integrated' -> no fetch, publish {} so readiness resolves.
//
// THE PC54 NULL-SUPPLIER CASE IS PRESERVED DELIBERATELY
// ----------------------------------------------------
// cp58/PC54 is a real SanMar product whose supplier_name is NULL (and
// whose is_smart_product is false -- the bad signal that caused the
// original P0). `supplierKeyForName` returns null for an absent name, so
// keying naively on it would re-break exactly the product the 2026-07-10
// fix repaired. An ABSENT supplier is therefore still treated as SanMar,
// which is what the name gate did and what the live data needs. This is a
// deliberate fail-OPEN on absence only; an unrecognized NON-EMPTY name
// still fails closed (no fetch), because a name we cannot resolve is not
// permission to guess.
//
// Reads the EFFECTIVE attribute so an unsaved supplier/SKU swap is honored
// the same way the SKU itself is.
const SUPPLIER_NAME_ABSENT_FALLBACK_KEY = "sanmar";

const effectiveSupplierName = computed(() =>
    (store.getEffectiveAttribute("supplier_name") ?? "").toString().trim(),
);

// The supplier_key to ask, accounting for the absent-name fallback above.
const resolvedSupplierKey = computed(() => {
    if (!effectiveSupplierName.value) {
        return SUPPLIER_NAME_ABSENT_FALLBACK_KEY;
    }
    return supplierKey.value;
});


const supplierCatalogState = computed(() => {
    // An absent supplier_name resolves without the vendor list at all, so
    // PC54 fetches on the first tick rather than waiting a round trip.
    if (!effectiveSupplierName.value) return "integrated";
    if (supplierOptions.value === null) return "pending";
    return hasCatalogIntegration(resolvedSupplierKey.value)
        ? "integrated"
        : "not-integrated";
});

// Publish the key for the editor's Artifi gates (Stage, filmstrip, Colors /
// Supplier panels, storefront renders -- `artifiPreviewEligible`). They hold
// only the supplier NAME, and "S&S Activewear" is not a name the old
// /sanmar/i gate could ever accept, so S&S products never asked Artifi for a
// preview. Published UNRESOLVED while the vendor list is in flight (the
// gates then answer from the name exactly as before), and resolved -- with
// or without a key -- once it lands.
//
// SYNC, not the default pre-flush: the Stage and the Supplier & SKU panel
// mount in the render that follows a product load, and their Artifi
// watchers run `immediate` during setup. A pre-flush publish had not run
// yet, so they read the absent-name SanMar fallback alongside the new SKU
// and asked Artifi about a keyless local supplier's SKU (two console 404s
// per load). Publishing synchronously keeps the store's key in step with
// the name it is derived from.
watch(
    () => [resolvedSupplierKey.value, supplierCatalogState.value !== "pending"],
    ([key, resolved]) => store.setSupplierKey(key, resolved),
    { immediate: true, flush: "sync" },
);

watch(
    () => [
        store.getEffectiveAttribute("product_item_number"),
        supplierCatalogState.value,
        store.getEffectiveAttribute("supplier_style_id"),
        // A swap that changes only the supplier (same style name at another
        // supplier) must refetch too: the swap clean-up waits on the NEW
        // catalog's answer.
        resolvedSupplierKey.value,
    ],
    ([sku, catalogState, styleId]) => {
        if (catalogState === "pending") {
            // The vendor list has not landed, so we cannot yet tell an
            // integrated supplier from a local one. Do NOTHING: no fetch,
            // and crucially no {} publish. Writing {} here would latch a
            // resolved-EMPTY catalog for a product about to resolve to
            // SanMar or S&S, and every downstream reader would conclude
            // permanently that it has no colors. Readiness stays false in
            // this window, which is the same state a real in-flight fetch
            // produces -- so the UI shows "Loading colors...", correctly,
            // and for a bounded time (loadSupplierOptions always settles,
            // including on error).
            return;
        }
        if (sku && catalogState === "integrated") {
            // A persisted S&S style id pins the style: its NAME is shared
            // across brands ("5200"). Unpinned products ask by name alone.
            if (styleId != null) meta.fetchMeta(sku, { styleId });
            else meta.fetchMeta(sku);
        } else if (catalogState === "integrated") {
            // Integrated, but there is no SKU to ask about yet (GH #3316).
            //
            // This case matched NEITHER arm before: not the fetch (no
            // sku), and not the publish below (it IS integrated). So
            // nothing was ever published, supplierMetaReady never
            // flipped, and ColorsPanel sat on "Loading colors..."
            // forever -- no error, no console message, nothing for the
            // user to report. It is the #2017 hang with a different
            // entry point, and it is newly reachable: the Create
            // Product CTA lands here on a stub, and a distributor who
            // picks the supplier before typing the SKU sits in exactly
            // this state.
            //
            // Publish {} for the same reason 'not-integrated' does:
            // readiness is `supplierMeta != null`, so an empty object is
            // how "resolved, and there is nothing to show yet" is
            // expressed. This is TRANSIENT, not a latch -- the watcher
            // re-runs when the SKU lands and takes the fetch arm above.
            //
            // The empty catalog on its own would be a LIE by omission
            // ("this product has no colors" is a different fact from
            // "you have not told me which product this is"), so
            // ColorsPanel reads the same no-SKU condition and says so
            // explicitly. The shell resolves the state; the panel
            // explains it.
            store.setSupplierMeta({});
            store.setSupplierMetaLoading(false);
        } else if (catalogState === "not-integrated") {
            // No catalog behind this supplier. Publish a resolved-EMPTY
            // meta so supplierMetaReady (= supplierMeta != null) flips true
            // and the cross-section readers (ColorsPanel, ColorFilmstrip,
            // LeftRail counts) resolve to an empty catalog instead of
            // hanging on "Loading colors..." (the #2017 loading-race,
            // inverted). The meta.meta watcher below only publishes
            // non-null payloads, so we write {} here directly and clear the
            // loading flag.
            store.setSupplierMeta({});
            store.setSupplierMetaLoading(false);
        }
    },
    { immediate: true },
);

// Publish resolved meta + its loading flag to the store so supplierMetaReady
// flips true on every tab once real data lands (immediate so the initial
// loading=false / meta=null baseline is written on open).
//
// For a suppressed (non-integrated) product the gate watcher above owns the
// meta state (it publishes {} so readiness flips true without a fetch). This
// producer must not clobber that {} back to null on its immediate fire, so it
// defers to the gate for those products. For an INTEGRATED product it writes
// the null baseline (correct: readiness stays false until the fetch resolves,
// the #2017 design) and then the resolved payload.
//
// 'pending' also defers -- writing the null baseline would be harmless, but
// staying silent keeps ONE writer per state and avoids a needless flip.
//
// INTEGRATED-BUT-NO-SKU DEFERS TOO (GH #3316). The gate above owns that
// state as well: it publishes {} because there is nothing to fetch yet.
// Without this clause the guard's `!== integrated` test does not fire --
// the state IS integrated -- so the producer wrote its null baseline
// straight over that {}. That is not theoretical; it is what the first
// fix attempt did for the absent-supplier product (PC54), whose state
// resolves to 'integrated' on the FIRST tick, before this watcher's
// immediate fire:
//
//   setSupplierMeta({})    <- gate resolves the hang
//   setSupplierMeta(null)  <- producer baseline, hang restored
//
// readiness flipped true and back to false, so ColorsPanel hung anyway.
// The rule this preserves is the one already stated above: exactly ONE
// writer per state. A fetch cannot be in flight without a SKU, so there
// is no payload for this watcher to contribute until one exists -- and
// when it does, the gate watcher takes the fetch arm and this watcher
// resumes owning the result.
watch(
    () => meta.meta.value,
    (newMeta) => {
        if (supplierCatalogState.value !== "integrated") return;
        if (!store.getEffectiveAttribute("product_item_number")) return;
        store.setSupplierMeta(newMeta);
    },
    { immediate: true },
);

// SYNC, for the same reason as the supplier-key publish above: the store's
// `supplierMetaSettled` (key resolved AND not loading) must never read
// "settled" while a request is in flight. With the default pre-flush timing
// the store kept loading=false for the rest of the flush in which the fetch
// began, so correctness rested on scheduler ordering. The variant cards'
// one-shot colorway auto-default gates on it.
watch(
    () => meta.loading.value,
    (isLoading) => {
        store.setSupplierMetaLoading(isLoading);
    },
    { immediate: true, flush: "sync" },
);

// Publish whether the fetch FAILED. The {} the gate watcher publishes (and a
// failed fetch's null meta, which never re-fires the meta watcher above)
// leave the store unable to tell "failed" from "empty"; this flag does. The
// composable clears `error` when a retry starts, so a later fetch clears it.
// NEVER express failure by writing null to supplierMeta: null means "not
// ready" to every other reader (#2003).
watch(
    () => !!meta.error.value,
    (failed) => {
        store.setSupplierMetaFailed(failed);
    },
    { immediate: true, flush: "sync" },
);

// ---------------------------------------------------------------------------
// Discard abandoned product stubs on leave (Jake, 2026-08-27).
//
// "When I create a new product on the Products page in the store editor and
// don't touch anything in that product and go back, it shouldn't be persisted.
// I only want the product to actually be created if the Supplier and SKU are
// filled in. If not, then warn when navigating out that the product will be
// removed/deleted with the option to cancel navigation."
//
// The Create Product CTA POSTs /campaign_product/create_stub and routes here
// with a real row already committed, so this is a discard-on-exit. The
// decision logic (what counts as abandoned, which navigations are internal,
// why the DELETE is awaited rather than fired during teardown) lives in
// discardStubOnLeave.js + abandonedProductStub.js; this block only supplies
// the live dependencies and hosts the dialog.
// ---------------------------------------------------------------------------
const vuexStore = useVuexStore();

// The color / image / name clean-up a confirmed SKU swap owes the product,
// run from THIS fetch so it completes whichever tab is showing. It used to
// live in the Supplier & SKU panel and was lost when the user left that tab
// before the new catalog loaded (card #349). A failed lookup is reported
// here too, never skipped silently.
useSkuSwapCleanup({
    store,
    meta: meta.meta,
    error: meta.error,
    notify: (message) => vuexStore.dispatch("raiseError", message),
    // The swap's fallback default color must obey the SAME coverage the
    // swatch grids use, so it never lands on a color that cannot be rendered.
    // Gated like the grids: only a supplier Artifi carries is asked.
    renderability: (sku) =>
        artifiPreviewEligible({
            supplierKey: store.supplierKey,
            supplierKeyResolved: store.supplierKeyResolved,
            supplierName: store.getEffectiveAttribute?.("supplier_name"),
        })
            ? awaitArtifiCoverage(sku)
            : Promise.resolve({ isUnsupported: () => false }),
});
const discardDialogOpen = ref(false);
// Whether the product being discarded carries the user's work (card #233):
// set by the guard each time it asks, so the dialog never says "nothing will
// be lost" about a product the user worked on.
const discardLosesWork = ref(false);
// The product the dialog is about, so the confirmation names what is deleted
// (card #542: a delete path must say WHICH product).
const discardProductName = ref("");
const discardDialogCopy = computed(() => {
    const name = (discardProductName.value ?? "").toString().trim();
    const subject = name ? `"${name}"` : "This product";
    return discardLosesWork.value
        ? {
            description:
                `${subject} has no supplier SKU, so it can't be sold. `
                + "Leaving now removes it from the store.",
            consequences: [
                `${name ? `"${name}"` : "The product"} and everything entered for it will be deleted.`,
                "Stay on this page to pick a supplier SKU instead.",
            ],
        }
        : {
            description:
                `${subject} has no supplier SKU yet, so it was never `
                + "fully created. Leaving now removes it from the store.",
            consequences: [
                `The empty product ${name ? `"${name}" ` : ""}will be deleted.`,
                "Nothing you have entered will be lost -- this product is empty.",
            ],
        };
});

// ---------------------------------------------------------------------
// EMPTY SHADES (Jake, 2026-10-21) -- see utils/emptyShade.js.
// ---------------------------------------------------------------------
const emptyShades = computed(() => emptyShadesOf(store.decorations));
const emptyShadeDialogOpen = ref(false);

// UNCOVERED COLORS (Jake, 2026-10-22): a product color that NO placement
// decorates. Same dialog, same non-blocking contract; the predicate is
// utils/uncoveredColors.js (its backend twin feeds the list/card icon). Not
// evaluated until the supplier catalog answer has settled (or if it failed):
// the hex that classifies a color light/dark arrives with it, and a
// name-only guess would warn about colors that are in fact covered. A
// supplier with no catalog integration settles with no color list, and its
// colors classify by name.
const uncoveredColors = computed(() => {
    if (store.supplierMetaSettled !== true || store.supplierMetaFailed === true) {
        return [];
    }
    const colors = selectedProductColors(
        store.getEffectiveAttribute("selected_color_skus"),
        store.supplierMeta?.colors,
    );
    return uncoveredColorsOf(store.decorations, colors, {
        manualLd: store.getEffectiveAttribute("manual_ld"),
        colorOverrides: store.getEffectiveAttribute("color_overrides"),
    });
});

// The one question the leave guard and the tab-close prompt ask.
const hasUndecoratedGarments = computed(
    () => emptyShades.value.length > 0 || uncoveredColors.value.length > 0,
);

const emptyShadeDescription = computed(() => {
    const parts = [];
    const n = emptyShades.value.length;
    if (n > 0) {
        parts.push(
            `${n === 1 ? "One placement has" : `${n} placements have`} a garment `
            + "color with no decoration.",
        );
    }
    const u = uncoveredColors.value.length;
    if (u > 0) {
        parts.push(
            `${u === 1 ? "One color has" : `${u} colors have`} no placement.`,
        );
    }
    parts.push("Orders for those garments are produced without a decoration there.");
    return parts.join(" ");
});

const emptyShadeConsequences = computed(() => {
    const lines = emptyShades.value.map(({ shade, placement }) => {
        const where = placement?.name || placement?.placement || "A placement";
        return `${where}: ${shade} garments get no decoration.`;
    });
    if (uncoveredColors.value.length > 0) {
        const names = uncoveredColors.value.map((c) => c.name || c.color_sku);
        lines.push(`No placement decorates: ${names.join(", ")}.`);
    }
    return lines;
});

// One shared pending decision, the same shape as the discard confirmation
// above (and for the same reason: overlapping guard calls must join one
// question, never orphan a resolver).
let emptyShadeDecisionPending = null;

function requestEmptyShadeConfirmation() {
    if (emptyShadeDecisionPending) return emptyShadeDecisionPending.promise;
    let settle;
    const promise = new Promise((resolve) => {
        settle = resolve;
    });
    emptyShadeDecisionPending = { promise, settle };
    emptyShadeDialogOpen.value = true;
    return promise;
}

function settleEmptyShadeDecision(leave) {
    const pending = emptyShadeDecisionPending;
    emptyShadeDecisionPending = null;
    emptyShadeDialogOpen.value = false;
    if (pending) pending.settle(leave);
}
// The IN-FLIGHT confirmation, as a promise plus its resolver. Held while the
// dialog is open so the guard's await settles exactly when the user chooses.
//
// This is deliberately ONE SHARED PROMISE rather than one resolver per caller.
// onBeforeRouteLeave can fire again while a confirmation is already open (the
// sidebar after the back arrow, a popstate landing on top of a router.push),
// and the previous shape kept a single resolver slot that the second call
// overwrote unconditionally. That made the first promise unreachable: it could
// never settle, vue-router waits on a leave guard, and the first navigation
// wedged for the rest of the session -- with the dialog's v-model flipped
// false, so it looked closed while the route stayed put.
//
// Returning the SAME promise to every overlapping caller closes that at the
// source: there is only ever one resolver, so none can be orphaned, and one
// user decision answers every pending guard. It is also the better behavior --
// two overlapping departures from the same product should ask the user once,
// not stack dialogs.
//
// NOTE (deliberate, Jake 2026-09-13): there is NO timeout here, and none may be
// added. A fail-open deadline would auto-navigate and leave the unconfigured
// product behind, and would override a user who was about to click "Stay on
// this page". Waiting on a HUMAN is unbounded by design; the navigation
// guarantee comes from the orphaning being impossible, not from bounding the
// wait. Pinned by ProductEditor.discardStubReentrancy.spec.js.
let discardDecisionPending = null;

// Set when the user confirmed DISCARDING this product on the way out: the
// empty-shade question is then moot (the product, and its placements, are
// being deleted), so the leave guard skips it.
let discardConfirmedThisLeave = false;

function settleDiscardDecision(discard) {
    if (discard) discardConfirmedThisLeave = true;
    const pending = discardDecisionPending;
    discardDecisionPending = null;
    discardDialogOpen.value = false;
    if (pending) pending.settle(discard);
}

function requestDiscardConfirmation({ authored = false, productName = "" } = {}) {
    // Already asking: join the open question rather than replacing it.
    if (discardDecisionPending) return discardDecisionPending.promise;

    discardLosesWork.value = authored === true;
    discardProductName.value = productName ?? "";

    let settle;
    const promise = new Promise((resolve) => {
        settle = resolve;
    });
    discardDecisionPending = { promise, settle };
    discardDialogOpen.value = true;
    return promise;
}

// The in-flight DELETE, shared the same way the confirmation is.
//
// One user decision now answers every overlapping guard invocation, so each of
// them proceeds to the delete for the SAME row. Without this, two departures
// issue two DELETEs: the first succeeds, the second hits an already-deleted
// row and reports through raiseError -- a spurious error toast for a user who
// did exactly one thing. Sharing the request means one decision produces one
// DELETE, and every caller awaits the same outcome.
let discardStubInFlight = null;

function discardStub(id) {
    if (discardStubInFlight) return discardStubInFlight;

    // Reuses the existing tenant-guarded delete endpoint (product.py
    // delete_product), which already special-cases stub CPs whose product_id
    // is NULL. `0` is its documented "no underlying products row to clean up"
    // sentinel -- an abandoned stub never has one.
    discardStubInFlight = axiosInstance
        .get(`/product/deleteProduct/${id}/0`)
        .catch((err) => {
            // Surface through the app's normal error channel, then let the
            // navigation proceed (the guard swallows the rejection). A failed
            // cleanup must never trap the user in the editor.
            // eslint-disable-next-line no-console
            console.error("Failed to discard abandoned product stub", describeError(err));
            vuexStore.dispatch("raiseError", err);
            throw err;
        });
    return discardStubInFlight;
}

function onDiscardConfirm() {
    settleDiscardDecision(true);
}

function onDiscardCancel() {
    settleDiscardDecision(false);
}

// The EFFECTIVE attribute bag (dirty-over-server). Effective, not serverState,
// so a SKU the user just picked counts immediately -- the autosave PATCH may
// still be in flight when they hit Back, and reading serverState alone would
// call a product they just gave a SKU "abandoned" and offer to delete it.
function currentEffectiveAttributes() {
    const serverAttrs = store.serverState?.data?.attributes;
    if (!serverAttrs) return null;
    // Union of the server payload's keys and every field the predicate can
    // key on, so a dirty edit still counts even when the server payload never
    // carried that column. ONE loop over ONE key set -- no second pass that
    // would silently mask a bug in the first.
    const keys = new Set([
        ...Object.keys(serverAttrs),
        ...ABANDONMENT_RELEVANT_FIELDS,
    ]);
    const merged = {};
    for (const key of keys) {
        const effective = store.getEffectiveAttribute(key);
        merged[key] = effective === undefined ? serverAttrs[key] : effective;
    }
    return merged;
}

const runStubDiscardGuard = createStubDiscardGuard({
    campaignProductId: () => campaignProductId.value,
    attributes: currentEffectiveAttributes,
    // The STICKY authoring-intent flag -- the primary discard gate. Read
    // through a getter so the guard sees its value at navigation time, not at
    // wiring time. Never derived from unsavedChangeCount: those counters drain
    // on autosave, so a configured product would read as untouched. See the
    // store's `_hasAuthoringIntent` comment.
    hasAuthoringIntent: () => store.hasAuthoringIntent === true,
    // Sticky "had a SKU" latch (card #542): a saved product whose SKU was
    // blanked is never offered for deletion.
    hasEverHadSku: () => store.hasEverHadSku === true,
    // Overlapping guard invocations SHARE one confirmation -- see
    // requestDiscardConfirmation. Never build the promise inline here: a fresh
    // promise per call is exactly the shape that orphaned the earlier one.
    requestConfirmation: requestDiscardConfirmation,
    deleteStub: (id) => discardStub(id),
});

onBeforeRouteLeave(async (to) => {
    discardConfirmedThisLeave = false;
    const result = await runStubDiscardGuard(to);
    // `false` cancels the navigation; anything else allows it.
    if (result === false) return false;
    // Leaving with an EMPTY SHADE: ask (non-blocking). Moving within this
    // product does not ask -- the user is still here and can fix it.
    if (
        isLeavingEditor(to, campaignProductId.value)
        && !discardConfirmedThisLeave
        && hasUndecoratedGarments.value
    ) {
        const leave = await requestEmptyShadeConfirmation();
        if (!leave) return false;
    }
    // Leaving THIS product: send any pending autosave BEFORE the navigation
    // resolves, because unmount then runs closeEditor, which wipes the dirty
    // Map and cancels the debounce. The store's flush also waits out a PATCH
    // already in flight, which is the case the unmount-time flush below
    // cannot cover. A tab / placement move stays in the editor, where
    // autosave carries on by itself, so it is not held up behind a slow
    // PATCH. A failed flush is surfaced by the store's error state; it does
    // not trap the user on the page.
    if (
        isLeavingEditor(to, campaignProductId.value)
        && typeof store.flushFieldAutoSave === "function"
    ) {
        try {
            await store.flushFieldAutoSave();
        } catch {
            // Intentionally swallowed -- see above.
        }
    }
    return true;
});

onUnmounted(() => {
    // No vendor-list retry outlives the editor that asked for it.
    supplierOptionsSource.dispose();
    if (typeof window !== "undefined") {
        window.removeEventListener("pagehide", onPageHide);
        window.removeEventListener("beforeunload", onBeforeUnload);
    }
    // Release a confirmation that is still open. The guard AWAITS
    // requestConfirmation(); if the component is torn down while the dialog
    // is up (a parent route change, an error boundary) nothing would ever
    // settle that promise and the router guard would hang forever, wedging
    // navigation for the rest of the session. Resolve it as "stay" -- an
    // unmount is not the user consenting to delete their product.
    settleDiscardDecision(false);
    // Same for the empty-shade question: an unmount is not "leave anyway".
    settleEmptyShadeDecision(false);

    // Field-only autosave (MVP): flush any settled-but-not-yet-debounced
    // scalar edit before closeEditor wipes the dirty Map. flushFieldAutoSave
    // dispatches the PATCH synchronously (the axios call captures the dirty
    // snapshot before any await), so the request goes out with the correct
    // body even though closeEditor clears state on the next line. Structural
    // changes are NOT flushed here -- those stay behind the explicit Save
    // button and are covered by the route-leave guard below.
    if (typeof store.flushFieldAutoSave === "function") {
        store.flushFieldAutoSave();
    }
    store.closeEditor();
    // closeEditor resets the productEditor store only. productEditorUiState
    // is a SEPARATE singleton that outlives this component, so without this
    // the selection (color, placement, camera, SKU views) leaked into the
    // next product the user opened -- most visibly as product B previewing
    // product A's color, which is a color B may not even have enabled.
    // Stage re-validates the color once B's enabled list loads; clearing
    // here closes the window before the wrong color is ever rendered.
    if (typeof editorUi.resetSelection === "function") {
        editorUi.resetSelection();
    }
    if (typeof document !== "undefined" && document.body) {
        document.body.classList.remove(FULLBLEED_BODY_CLASS);
    }
});

function retry() {
    store.loadServerState();
}
</script>

<style scoped>
.product-editor-root {
    display: flex;
    flex-direction: column;
    /* Whole-page scroll (2026-07-20, Jake): "the whole editor scrolls,
       nothing pinned." The editor root grows to its natural content height
       and the OUTER `.dist-main` (the app-shell main region) owns the page
       scroll. It is NOT a fixed-height, overflow-hidden shell anymore --
       that trapped the Sizes & Colors content in a tiny inner scroller on
       laptop-height viewports. `min-height: 100%` keeps it filling the
       viewport when content is short; content taller than the viewport
       pushes the page scrollbar (the outer `.dist-main` one) instead of an
       inner box. */
    min-height: 100%;
}

.product-editor-header {
    flex: 0 0 auto;
}

.product-editor-body {
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    /* No min-height: 0 -- the row is allowed to grow to the tallest column
       so the whole thing scrolls as one page (no inner clipping). */
}

.product-editor-rail {
    flex: 0 0 280px;
    width: 280px;
    /* No inner overflow: the rail scrolls with the page (Jake: nothing
       pinned). A tall rail extends the page height, which is acceptable
       per the whole-page-scroll decision. */
    border-right: 1px solid var(--tf-border, #e0e0e0);
}

.product-editor-right {
    flex: 0 0 340px;
    width: 340px;
    /* No inner overflow: the right rail scrolls with the page too. */
    border-left: 1px solid var(--tf-border, #e0e0e0);
    padding: 16px;
}

/*
  Day 6 known viewport-squeeze: the OUTER distributor app shell already
  consumes ~220px on the left (Stores / Decoration Library / etc.
  sidebar). With our 280px LeftRail + 340px right rail + outer 220px,
  the available middle column at a 1280px viewport collapses to
  ~440px and at 1024px falls below 200px where Stage/RoutingStrip/
  ColorFilmstrip stack. With whole-page scroll (2026-07-20) the vertical
  squeeze is resolved -- content flows into page height instead of a
  trapped inner scroller. The horizontal 3-column budget is unchanged;
  the editor still reads best at 1440px+.
*/

.product-editor-main {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
    /* No min-height: 0 -- the main column grows to its content height so
       the ContextPanel (Sizes & Colors etc.) is never crushed into a
       sliver; it flows into the page scroll instead. */
}

.product-editor-stage {
    flex: 0 0 auto;
    /* Enlarged canvas (2026-07-22, Jake, #2220): match the Sizes & Colors
       split canvas size on the placements path too, so the preview is the
       same (larger) size everywhere. Same clamp as .product-editor-stage--split
       below. Part of the scrolling page flow (scrolls away with content, not
       pinned). object-fit: contain in Stage keeps the 800x1200 render from
       cropping/upscaling.

       The clamp sizes the PREVIEW AREA inside the Stage, not the whole tile
       (2026-10-28): the slim "Upload your image" strip under the preview adds
       its own row on top, so the preview keeps exactly the size it always had
       and the strip never squeezes it or the notices drawn in it. */
    height: auto;
}
.product-editor-stage :deep(.stage-preview-area) {
    flex: 0 0 auto;
    height: clamp(380px, 48vh, 520px);
}
/* While the fallback options show, the tile sizes to its content (Stage.vue). */
.product-editor-stage.stage-root--content-sized :deep(.stage-preview-area) {
    height: auto;
}

.product-editor-context {
    flex: 1 1 auto;
    /* No inner overflow: the setup section (Sizes & Colors, Pricing, etc.)
       flows into the page scroll instead of a trapped inner scroller. */
    padding: 16px 24px;
}

/*
  Sizes & Colors split (2026-07-20, Jake; row-2 follow-up to #2183/#2185). On
  the merged Sizes & Colors tab the middle column is two stacked rows:
    ROW 1 (.product-editor-split-row-top): a 2-col grid -- ENLARGED preview
      canvas + ColorFilmstrip on the left, SizesPanel on the right.
    ROW 2 (.product-editor-split-colors): ColorsPanel, full-width below.
  The color list can grow tall (some SKUs have many colors), so it gets its own
  full-width row rather than being squeezed into the narrow sizes column. Both
  rows share the same 16px/24px padding so the full-width colors row aligns
  with the grid row's gutters.
*/
.product-editor-sizes-colors-split.is-split-v2 {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

/*
  Row 1: the preview column is fixed-ish so the ENLARGED canvas gets a generous
  share of the width for the 800x1200 Artifi render; the sizes column takes the
  remaining space with a minimum so the size chips stay usable. `minmax(0, ...)`
  on both tracks lets the grid shrink below content width (min-width:0 escape)
  so it never forces a horizontal scrollbar on the page.
*/
.product-editor-split-row-top {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(320px, 1fr);
    gap: 24px;
    align-items: start;
    padding: 16px 24px;
    min-width: 0;
}

.product-editor-split-preview {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.product-editor-split-sizes {
    min-width: 0;
}

/*
  Row 2: ColorsPanel full-width below the grid row, sharing row 1's horizontal
  gutter (24px) so it aligns; a little top padding separates it from the row
  above. min-width:0 keeps a wide swatch grid from forcing horizontal overflow.
*/
.product-editor-split-colors {
    min-width: 0;
    padding: 8px 24px 16px;
}

/*
  The enlarged canvas: in the split layout the Stage grows beyond the stacked
  360px so the higher-resolution render is shown large -- but smaller than the
  first-pass clamp (the color list now owns its own row, so the canvas need not
  dominate the fold). It is capped by a max-height and by the column width
  (Stage's image uses object-fit: contain, so it never crops or upscales past
  the 800x1200 source). Height tracks the viewport so tall desktop windows get
  a bigger canvas without overflowing short laptop screens.
*/
.product-editor-stage--split {
    height: auto;
}
.product-editor-stage--split :deep(.stage-preview-area) {
    height: clamp(380px, 48vh, 520px);
}
.product-editor-stage--split.stage-root--content-sized :deep(.stage-preview-area) {
    height: auto;
}

/*
  The sizes column drops the wide horizontal padding .product-editor-context
  carries in the stacked layout -- the grid gap + row padding already provide
  the gutters, and the narrower column reads better flush.
*/
.product-editor-split-sizes.product-editor-context {
    padding: 0;
}

/*
  Responsive fallback: below a sensible breakpoint the two columns would each
  be too narrow to be useful and the sizes chips would wrap awkwardly, so row 1
  collapses to a single stacked column (preview above sizes); the colors row
  stays full-width below. This guarantees no horizontal overflow on tablet /
  narrow-desktop widths. The 1100px threshold accounts for the outer
  distributor sidebar (~220px) + the 280px LeftRail already consumed before
  this middle column.
*/
@media (max-width: 1100px) {
    .product-editor-split-row-top {
        grid-template-columns: minmax(0, 1fr);
    }

    .product-editor-stage :deep(.stage-preview-area),
    .product-editor-stage--split :deep(.stage-preview-area) {
        height: clamp(360px, 42vh, 440px);
    }
    .product-editor-stage.stage-root--content-sized :deep(.stage-preview-area),
    .product-editor-stage--split.stage-root--content-sized :deep(.stage-preview-area) {
        height: auto;
    }

    .product-editor-split-sizes.product-editor-context {
        padding: 16px 0 0;
    }
}

.product-editor-banner {
    flex: 0 0 auto;
}

.product-editor-review-banner {
    flex: 0 0 auto;
}
</style>

<!--
  Day 8 Item B1 - global escape from DistributorLayout's `.dist-main`
  padding/overflow while the product editor is mounted.

  Why unscoped: this selector intentionally reaches OUT of the component
  to the parent layout shell. A scoped `body.tf-product-editor-active`
  selector would still scope the *descendant* selector and miss
  `.dist-main` (which lives in DistributorLayout, not ProductEditor).

  Why this is safe:
    - The body class is added/removed by this component's lifecycle, so
      the rule only applies while ProductEditor is mounted.
    - It targets a single, well-known DistributorLayout class.
    - No other route mounts ProductEditor, so the class never bleeds.

  Pattern reference: StoreEditShell uses `body.tf-role-client` for the
  same kind of layout-shell escape.
-->
<style>
/*
  Drop ONLY the `.dist-main` padding so the editor's header/footer are
  full-bleed. We intentionally do NOT override `.dist-main`'s overflow --
  its base rule keeps `overflow-y: auto`, so the OUTER app-shell main region
  owns the page scroll and the whole editor scrolls as one column (2026-07-20,
  Jake: "the whole editor scrolls, nothing pinned"). The previous
  `overflow: hidden` here killed the page scroll and forced the editor body
  to self-clip, which trapped the Sizes & Colors content in a tiny inner
  scroller on laptop-height viewports.
*/
body.tf-product-editor-active .dist-main {
    padding: 0;
}
</style>
